/*****************************************************************************/
/*  File Name: itemSelect.js                                                 */
/*  Date:      June 22, 2023                                                 */
/*  Author:    Christopher Cressman                                          */
/*  Purpose:   This will handle the item selections of the systems on the
/*             bottom half of the bar
/*
/*****************************************************************************/

import React /* useState,*/ from 'react';
import { withTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import styles from './styles/roi.module.scss';
import { MenuItem, Button, IconButton, Tooltip, Typography } from '@mui/material';
import { useROIContext } from '../../context/roiContext';

import { convertToCurrency } from '../../functions/convertCurrency';
import { useTipContext } from '../../context/tipContext';
import { convertToNumber } from '../../functions/convertNumber';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import SoltaSelect from './elements/SelectField';
import TipPriceTextField from './elements/TipPriceField';
import * as TIMERANGE from '../../constants/timeRanges';
import { useSystemContext } from '../../context/systemContext';

function TipSelectUpgrade({ t }) {
  //Load all of the default component varaibles

  const { upgradeTips,
    setUpgradeTips,
    addAnotherTreatmentUpgrade,
    removeTreatmentUpgrade,
    addTipCountUpgrade,
    removeTipCountUpgrade, selectedTimeRange, consumablePriceUpgrade } = useROIContext();

  const { tips, tipsLoading } = useTipContext();

  const { upgradeSystem } = useSystemContext()

  //console.log('this is my upgrade system --->', upgradeSystem)
  //console.log('this is my upgradeed tips ---->', upgradeTips)



  if (upgradeSystem) {
    return (
      <Grid item xs={12} container className={`${styles.tipSelectGrid} ${styles.upgradeTips}`} >
        <Grid item xs={12} container className={`${styles.tipHeadline} ${styles.upgradeHeader}`} justifyContent='space-between'>
          <Grid item xs={9} className={styles.title} container gap={2}>
            <Grid item>
              {upgradeSystem.logo ? <img src={upgradeSystem.logoURL} alt={upgradeSystem.name} id={upgradeSystem.id} className={styles.brandLogo} /> : ''}
            </Grid>
            <Grid item>
              {upgradeSystem.name} Treatments
            </Grid>


          </Grid>
          <Grid item>
            <Button className={styles.addTreatmentButton} onClick={() => addAnotherTreatmentUpgrade()} startIcon={<AddCircleOutlineIcon />}>
              Add treatment
            </Button>
          </Grid>
        </Grid>

        <Grid item xs={12} container alignItems='flex-end' className={`${styles.tipLine} ${styles.headline}`}>
          <Grid item className={styles.removeGrid}>
            {/* Remove button place holder*/}
          </Grid>
          <Grid item xs className={styles.tipSelectDropDown}>
            Treatment<br /> Tip
          </Grid>
          <Grid item xs className={styles.quantityGrid}>
            Treatments<br /> Per {selectedTimeRange === TIMERANGE.weekly ? 'Week' : 'Month'}
          </Grid>
          <Grid item xs className={`${styles.tipPriceGrid} `}>
            Treatment Price<br /> to patient
          </Grid>
          <Grid item xs className={`${styles.tipPriceGrid} `}>
            Tip Price<br /> Per Treatment
          </Grid>
          <Grid item xs className={`${styles.tipPriceGrid}`}>
            Other<br /> Charges
          </Grid>
        </Grid>
        <Grid item xs={12} container className={styles.tipGroup}>
          {!tipsLoading
            ? upgradeTips.map((myTip, i) => (
              <Grid item xs={12} container key={i} alignItems='center' className={styles.tipLine}>
                <Grid item className={styles.removeGrid}>
                  <Tooltip title='Remove Item?'>
                    <IconButton onClick={() => removeTreatmentUpgrade(i)} className={styles.removeButton}>
                      <RemoveCircleOutlineIcon />
                    </IconButton>
                  </Tooltip>
                </Grid>
                <Grid item xs className={styles.tipSelectDropDown}>
                  <SoltaSelect
                    labelId='tip-select'
                    id='tip-select'
                    value={upgradeTips[i].id}
                    onChange={(event) =>
                      setUpgradeTips((prevupgradeTips) => {
                        const updatedupgradeTips = [...prevupgradeTips]; // Create a new array with the same values as prevupgradeTips

                        //Get all of the tips information
                        const selectedTip = tips.filter((tip) => tip.id === event.target.value).map((tip) => tip);
                        // Update the second entry's sku value
                        updatedupgradeTips[i] = selectedTip[0];
                        updatedupgradeTips[i].amount = 1; //Set the default quantiy to 1

                        return updatedupgradeTips; // Return the updated array as the new state
                      })
                    }
                  >
                    {tips.map((tip) => (
                      tip.system === upgradeSystem.id ? (
                        <MenuItem value={tip.id} key={tip.id}>
                          <Typography className={styles.tipName}>{tip.sku}</Typography>
                        </MenuItem>
                      ) : null

                    ))}
                  </SoltaSelect>
                </Grid>
                <Grid item xs container alignItems='center' justifyContent='center' className={styles.quantityGrid}>
                  <Grid item xs className={styles.quantityButtons} alignSelf='center'>
                    <Tooltip title='Subtract Quantity'>
                      <span>
                        <IconButton disabled={convertToNumber(myTip.amount ?? 0) <= 1} className={styles.quantityButton} onClick={() => removeTipCountUpgrade(i)}>
                          <RemoveCircleOutlineIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Grid>
                  <Grid item xs className={styles.quantityField} alignSelf='center'>
                    <TipPriceTextField
                      value={myTip.amount ?? 0}
                      onChange={(event) =>
                        setUpgradeTips((prevupgradeTips) => {
                          const updatedupgradeTips = [...prevupgradeTips];
                          updatedupgradeTips[i].amount = convertToNumber(event.target.value);
                          return updatedupgradeTips;
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs className={styles.quantityButtons}>
                    <Tooltip title='Add Quantity'>
                      <span>
                        <IconButton disabled={!myTip.id} className={styles.quantityButton} onClick={() => addTipCountUpgrade(i)}>
                          <AddCircleOutlineIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Grid>
                </Grid>
                <Grid item xs className={`${styles.tipPriceGrid} ${styles.price}`}>
                  <TipPriceTextField
                    value={convertToCurrency(myTip.treatmentPrice) ?? 0}
                    onChange={(event) =>
                      setUpgradeTips((prevupgradeTips) => {
                        const updatedupgradeTips = prevupgradeTips.map((tip) => ({ ...tip }));
                        updatedupgradeTips[i].treatmentPrice = convertToNumber(event.target.value);
                        return updatedupgradeTips;
                      })
                    }
                  />
                  {/* ${myTip.treatmentPrice ? convertToCurrency(myTip.treatmentPrice) : 0} */}
                </Grid>
                <Grid item className={`${styles.tipPriceGrid} ${styles.price}`}>
                  ${convertToCurrency(consumablePriceUpgrade(i))}
                </Grid>
                <Grid item xs className={`${styles.tipPriceGrid}`}>
                  <TipPriceTextField
                    value={myTip.other ?? 0}
                    onChange={(event) =>
                      setUpgradeTips((prevupgradeTips) => {
                        const updatedupgradeTips = [...prevupgradeTips];
                        updatedupgradeTips[i].other = convertToNumber(event.target.value);
                        return updatedupgradeTips;
                      })
                    }
                  />
                </Grid>
              </Grid>
            ))
            : 'Please select a machine'}
        </Grid>
      </Grid>
    );
  } else {
    return null;
  }




}

export default withTranslation()(TipSelectUpgrade);
